import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ModalSettings } from './ModalSettings'
import { Loader } from '../common/spinner/Loader'
import { ModalUpsertProtectedAssetGoal } from './ModalUpsertProtectedAssetGoal'
import { Button, makeStyles, tokens } from '@fluentui/react-components'
import { AddCircle24Regular } from '@fluentui/react-icons'
import { t } from 'i18next'
import { Process, ProtectedAssetGoal, ProtectedAssetGoalType, Workplace } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { api } from '../../helpers/apiHelper'
import { mapWorkplacesSNIs, getProtectedAssetsOrganisationalGoalsColumns, getProtectedAssetGoalUsage } from '../../helpers/settingsHelper'
import { handleMessage } from '../../helpers/stateHelper'
import { formatToSwedishCrowns } from '../../helpers/stringHelper'
import { CustomList } from '../common/list/CustomList'
import { InfoMessageBar } from '../common/other/InfoMessageBar'
import { IOpenCloseModalProps } from '../../interfaces/IOpenCloseModalProps'
import { Text } from '../common/text/Text'

const useStyles = makeStyles({
   addButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: tokens.spacingVerticalL,
      marginRight: tokens.spacingHorizontalS,

      '@media screen and (max-width: 1007px)': {
         padding: tokens.spacingVerticalM,
      },
   },
   addButtonPhone: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '30%',
         },
         ':nth-child(2)': {
            width: '30%',
            minWidth: '300px',
         },
         ':nth-child(3)': {
            width: '15%',
            minWidth: '150px',
         },
         ':nth-child(4)': {
            width: '15%',
            minWidth: '150px',
         },
         ':nth-child(5)': {
            width: '15%',
            minWidth: '150px',
         },
      },
   },
   phoneItem: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'flex',
      },
      flexDirection: 'column',
      padding: `0 ${tokens.spacingHorizontalS}`,
      borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
      paddingBottom: tokens.spacingVerticalL,
      '& > *': {
         marginBottom: tokens.spacingVerticalXXS,
      },
   },
})

type ModalProtectedAssetGoalProps = IOpenCloseModalProps & {
   isProtectedAsset: boolean
}

export const ModalProtectedAssetGoal = ({ open, closeModal, isProtectedAsset }: ModalProtectedAssetGoalProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const {
      state: { protectedAssetGoals },
   } = dataContext
   const { hasMsalToken, organisation } = dataContext.state
   const [openModal, setOpenModal] = useState<boolean>(false)
   const [selectedItem, setSelectedItem] = useState<ProtectedAssetGoal>(null)
   const [allProcesses, setAllProcesses] = useState<Process[]>([])
   const [allWorkplaces, setAllWorkplaces] = useState<Workplace[]>([])
   const [protectedAssetGoalInUseMap, setProtectedAssetGoalInUseMap] = useState<Record<string, boolean>>({})
   const [loading, setLoading] = useState(true)

   const selectedItems = useMemo(() => {
      return protectedAssetGoals.filter(
         (item) => item.type === (isProtectedAsset ? ProtectedAssetGoalType.ProtectedAsset : ProtectedAssetGoalType.OrganisationalGoal)
      )
   }, [protectedAssetGoals, isProtectedAsset])

   useEffect(() => {
      if (!open) return
      getProtectedAssetGoalUsage(selectedItems, setProtectedAssetGoalInUseMap)
   }, [selectedItems, open])

   const onRowClick = (item: ProtectedAssetGoal) => {
      setOpenModal(true)
      setSelectedItem(item)
   }

   useEffect(() => {
      if (!open) return
      ;(async () => {
         try {
            const [processesResponse, allSnis, workplacesResponse] = await Promise.all([
               api().getAllProcesss(),
               (await api().getAllSnis()).data,
               api().getWorkplacesByOrganisationId(organisation.id),
            ])
            const mappedWorkplaces = mapWorkplacesSNIs(workplacesResponse.data, allSnis)

            setAllProcesses(processesResponse.data)
            setAllWorkplaces(mappedWorkplaces)
         } catch (error) {
            handleMessage(dataContext.setRootState, 'error', t('SomethingWentWrong'), t('CouldNotFetchData'))
         } finally {
            setLoading(false)
         }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, organisation.id])

   return (
      <>
         <ModalSettings open={open} closeModal={closeModal} title={isProtectedAsset ? t('ProtectedAssets') : t('OrganisationalGoals')}>
            {loading ? (
               <Loader />
            ) : (
               <>
                  <div className={classes.addButton}>
                     <Button
                        onClick={() => onRowClick(null)}
                        appearance="primary"
                        disabled={false}
                        className={classes.addButtonPhone}
                        icon={<AddCircle24Regular />}
                     >
                        {t('Add')}
                     </Button>
                  </div>

                  {selectedItems.length > 0 ? (
                     <>
                        <CustomList
                           className={classes.hideInPhone}
                           columns={getProtectedAssetsOrganisationalGoalsColumns()}
                           items={selectedItems}
                           onRowClick={onRowClick}
                           rowClassName={classes.row}
                        />
                        {selectedItems.map((item, index) => (
                           <div className={classes.phoneItem} key={index} onClick={() => onRowClick(item)}>
                              <Text size={400} weight="semibold">
                                 {item.name}
                              </Text>
                              <Text size={400}>{`${formatToSwedishCrowns(item.valuation)} kr`}</Text>
                              <Text size={400}>{item.process}</Text>
                              <Text size={400} ellipsis>
                                 {item.description}
                              </Text>
                           </div>
                        ))}
                     </>
                  ) : (
                     <InfoMessageBar
                        title={t('TimeToCreateYourFirstItem', {
                           Item: isProtectedAsset ? t('ProtectedAsset').toLowerCase() : t('OrganisationalGoal').toLowerCase(),
                        })}
                     />
                  )}
               </>
            )}
         </ModalSettings>

         <ModalUpsertProtectedAssetGoal
            isProtectedAsset={isProtectedAsset}
            selectedItem={selectedItem}
            open={openModal}
            setOpen={setOpenModal}
            allProcesses={allProcesses}
            allWorkplaces={allWorkplaces}
            isProtectedAssetGoalInUseMap={protectedAssetGoalInUseMap}
         />
      </>
   )
}
